import { Button, Divider, ListItemText, ListSubheader, Menu, MenuItem } from '@mui/material'
import { GRANT_FIXED_LEVELS } from '@omnea-digital/authz-model'
import { useTranslation } from 'react-i18next'
import { ChevronDownOMIcon } from '@omnea-app/icons'
import { useMenu } from '@omnea-app/omui/useMenu'
import { FIXED_LEVEL_LABEL_KEYS } from '../permissionLabels'
import { configurationKey } from './recordGrantDiff'
import type { RecordGrantConfiguration } from './useUpdateRecordAccessGrant'

type PermissionSetOption = {
  id: string
  name: string
}

type ConfigurableLevel = {
  kind: 'configurable'
  configuration: RecordGrantConfiguration
  permissionSets: readonly PermissionSetOption[]
  /** Sets named by the grants already on the record, which the catalogue need not still offer. */
  heldSets: readonly PermissionSetOption[]
  /** The endpoint rejects a grant naming an archived user, so such a row offers removal alone. */
  canOnlyBeRemoved: boolean
  onChange: (configuration: RecordGrantConfiguration) => void
}

/** For a grant that confers exactly one thing, so there is no level to choose. */
type LabelOnlyLevel = {
  kind: 'label-only'
  label: string
}

type Props = {
  level: ConfigurableLevel | LabelOnlyLevel
  disabled: boolean
  subjectName: string
  onRemove: () => void
}

type ConfigurationOptionsProps = {
  level: ConfigurableLevel
  onSelected: () => void
}

const ConfigurationOptions = ({ level, onSelected }: ConfigurationOptionsProps) => {
  const { t } = useTranslation('settings', { keyPrefix: 'record-access' })
  const { t: permissionSetsT } = useTranslation('settings', { keyPrefix: 'permission-sets' })
  const selectedKey = configurationKey(level.configuration)

  const selectConfiguration = (chosen: RecordGrantConfiguration) => {
    onSelected()
    if (configurationKey(chosen) !== selectedKey) level.onChange(chosen)
  }

  return (
    <>
      {GRANT_FIXED_LEVELS.map((fixedLevel) => (
        <MenuItem
          key={fixedLevel}
          selected={configurationKey({ kind: 'fixed', level: fixedLevel }) === selectedKey}
          onClick={() => selectConfiguration({ kind: 'fixed', level: fixedLevel })}
        >
          <ListItemText primary={permissionSetsT(FIXED_LEVEL_LABEL_KEYS[fixedLevel])} />
        </MenuItem>
      ))}
      {level.permissionSets.length > 0 && <ListSubheader>{t('custom-sets-heading')}</ListSubheader>}
      {level.permissionSets.map((set) => (
        <MenuItem
          key={set.id}
          selected={configurationKey({ kind: 'custom', permissionSetId: set.id }) === selectedKey}
          onClick={() => selectConfiguration({ kind: 'custom', permissionSetId: set.id })}
        >
          <ListItemText primary={set.name} />
        </MenuItem>
      ))}
      <Divider />
    </>
  )
}

export const RecordAccessLevelMenu = ({ level, disabled, subjectName, onRemove }: Props) => {
  const { t } = useTranslation('settings', { keyPrefix: 'record-access' })
  const { t: permissionSetsT } = useTranslation('settings', { keyPrefix: 'permission-sets' })
  const { anchorEl, open: openMenu, close: closeMenu, isOpen } = useMenu()

  const configurationLabelOf = (configurableLevel: ConfigurableLevel): string => {
    if (configurableLevel.configuration.kind === 'fixed') {
      return permissionSetsT(FIXED_LEVEL_LABEL_KEYS[configurableLevel.configuration.level])
    }
    const { permissionSetId } = configurableLevel.configuration
    const permissionSetName = [...configurableLevel.permissionSets, ...configurableLevel.heldSets].find(
      (set) => set.id === permissionSetId
    )?.name
    return permissionSetName ?? t('unknown-configuration')
  }

  const buttonLabel = level.kind === 'label-only' ? level.label : configurationLabelOf(level)

  return (
    <>
      <Button
        size="small"
        color="secondary"
        variant="text"
        endIcon={<ChevronDownOMIcon />}
        disabled={disabled}
        aria-label={t('configuration-aria', { name: subjectName })}
        onClick={openMenu}
      >
        {buttonLabel}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={isOpen}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {level.kind === 'configurable' && !level.canOnlyBeRemoved && (
          <ConfigurationOptions level={level} onSelected={closeMenu} />
        )}
        <MenuItem
          color="danger"
          aria-label={t('remove-aria', { name: subjectName })}
          onClick={() => {
            closeMenu()
            onRemove()
          }}
        >
          <ListItemText primary={t('remove-label')} />
        </MenuItem>
      </Menu>
    </>
  )
}
